import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { generatePath } from 'react-router-dom';
import { Pagination, Response } from '@ui/src';
import { Lock, LockAuditLog } from '../../types';
import { securityApi } from '../services';

import ROUTES from '../../../../routes';

export interface SecurityLocksState {
  locks?: Lock[];
  pagination?: Pagination;
  lockItem?: Lock;
  lockItemAuditLogs?: LockAuditLog[];
  lockItemAuditLogsPagination?: Pagination;
  takeCount?: number;
  lockItemUser?: {
    id: string;
    profile: {
      firstName: string;
      lastName: string;
    };
  };
}

const initialState: SecurityLocksState = {};

export const securityLocksSlice = createSlice({
  name: 'securityLocks',
  initialState,
  reducers: {
    setTakeCount: (state, action: PayloadAction<number>) => {
      state.takeCount = action.payload;
    },
    clearLocksState: (state) => {
      state.locks = undefined;
      state.pagination = undefined;
      state.takeCount = undefined;
    },
    clearLockPanelState: (state) => {
      state.lockItem = undefined;
      state.lockItemAuditLogs = undefined;
      state.lockItemUser = undefined;
      state.lockItemAuditLogsPagination = undefined;
    },
  },
  extraReducers: (builder) => {
    builder.addMatcher(
      securityApi.endpoints.getLocks.matchFulfilled,
      (state, action: PayloadAction<Response<Lock>>) => {
        state.locks = action.payload.data.edges.map((item) => ({
          ...item.node,
          url: generatePath(ROUTES.SECURITY_LOCKS_ITEM, {
            lockId: item.node.id,
          }),
          type: item.node.createdByEmployeeId ? 'by Employee' : 'Auto',
        }));

        state.pagination = action.payload.data.pageInfo;
      },
    );
    builder.addMatcher(
      securityApi.endpoints.getLockItem.matchFulfilled,
      (state, action: PayloadAction<{ data: Lock }>) => {
        state.lockItem = action.payload.data;
      },
    );
    builder.addMatcher(
      securityApi.endpoints.getLockItemUser.matchFulfilled,
      (
        state,
        action: PayloadAction<{
          data: {
            id: string;
            profile: {
              firstName: string;
              lastName: string;
            };
          };
        }>,
      ) => {
        state.lockItemUser = action.payload.data;
      },
    );
    builder.addMatcher(
      securityApi.endpoints.updateLock.matchFulfilled,
      (state, action: PayloadAction<{ data: Lock }>) => {
        state.locks = state.locks?.map((item) => {
          let result = { ...item };

          if (result.id === action.payload.data.id) {
            result = {
              ...result,
              ...action.payload.data,
            };
          }

          return result;
        });

        state.lockItem = action.payload.data;
      },
    );
    builder.addMatcher(
      securityApi.endpoints.getLockItemAuditLogs.matchFulfilled,
      (state, action: PayloadAction<Response<LockAuditLog>>) => {
        if (state.lockItem) {
          state.lockItemAuditLogs = action.payload.data.edges.map(
            (item) => item.node,
          );

          state.lockItemAuditLogsPagination = action.payload.data.pageInfo;
        }
      },
    );
  },
});

export const { clearLocksState, clearLockPanelState, setTakeCount } =
  securityLocksSlice.actions;

export default securityLocksSlice.reducer;
